import { useMemo } from 'react';
import {
  Combobox,
  ComboboxItem,
  ComboboxList,
  PopoverArrow,
  Select,
  SelectItem,
  SelectLabel,
  SelectPopover,
  useComboboxStore,
  useSelectStore,
} from '@ariakit/react';
import type { ClassValue } from 'clsx';
import clsx from 'clsx';

import { IconSolidCheckCircle, IconSolidSearch, IconSolidXCircle } from '../../../components/icons';
import { vars } from '../../../css/vars';
import { Box } from '../Box/Box';
import { ButtonIcon } from '../ButtonIcon/ButtonIcon';
import { Stack } from '../Stack/Stack';
import { Text } from '../Text/Text';

import {
  checkbox,
  comboboxHasResults,
  comboboxList,
  comboboxWrapper,
  selectButton,
  selectItem,
  selectLabel,
  selectPopover,
  statePlaceholder,
  combobox as styles_combobox,
  disabled as styles_disabled,
} from './styles.css';

type Option = {
  key: string;
  render: React.ReactNode;
};

type Props<T extends string | string[]> = {
  label: string;
  icon?: React.ReactNode;
  value: T | undefined;
  valueRender?: React.ReactNode;
  options: Option[] | undefined;
  onChange: (value: any) => void;
  onChangeQuery?: (value: string) => void;
  onClose?: () => void;
  queryPlaceholder?: string;
  cssClass?: ClassValue | ClassValue[];
  wrapperCssClass?: ClassValue | ClassValue[];
  popoverCssClass?: ClassValue | ClassValue[];
  creatableRender?: (key: string) => React.ReactNode | undefined;
  defaultOpen?: boolean;
  disabled?: boolean;
  loading?: boolean;
  loadingRender?: React.ReactNode;
  emptyStateRender?: React.ReactNode;
  clearable?: boolean;
  editInPlace?: boolean;
};

export const ComboboxSelect = <T extends string | string[]>({
  label,
  icon,
  value,
  valueRender,
  options,
  onChange,
  onChangeQuery,
  onClose,
  queryPlaceholder,
  cssClass,
  wrapperCssClass,
  popoverCssClass,
  creatableRender,
  defaultOpen,
  disabled,
  loading,
  loadingRender,
  emptyStateRender,
  clearable,
  editInPlace,
}: Props<T>) => {
  const isMultiselect = typeof value === 'object';

  const combobox = useComboboxStore({
    setValue: (newValue) => {
      onChangeQuery?.(newValue);
    },
    resetValueOnHide: !editInPlace,
    defaultOpen,
  });

  const select = useSelectStore({
    combobox,
    setValue: (newValue: T) => {
      onChange(newValue);
      if (editInPlace && typeof newValue === 'string') {
        combobox.setValue(newValue);
      }
      if (isMultiselect && defaultOpen) {
        combobox.setOpen(false);
      }
    },
    value,
  });

  const valueSet = new Set(value);

  const query = combobox.useState('value');

  const isLoading = (loading ?? false) || options === undefined;

  const shouldTruncateValueRender = typeof valueRender === 'string';

  const allOptions = useMemo(() => {
    const optionMap = new Map<string, Option>();

    if (query !== undefined && query !== '' && creatableRender !== undefined) {
      optionMap.set(query, { key: query, render: creatableRender(query) });
    }
    if (isMultiselect && creatableRender) {
      value?.forEach((v) => {
        if (v !== query) {
          optionMap.set(v, { key: v, render: creatableRender(v) });
        }
      });
    }
    options?.forEach((option) => optionMap.set(option.key, option));

    return Array.from(optionMap.values());
  }, [query, value, options, creatableRender, isMultiselect]);

  return (
    <div className={clsx(wrapperCssClass)}>
      <SelectLabel store={select} className={selectLabel}>
        {label}
      </SelectLabel>
      <Select
        store={select}
        className={clsx([
          selectButton,
          cssClass,
          {
            [styles_disabled]: disabled,
          },
        ])}
        disabled={disabled}
      >
        <Stack direction="row" align="center" justify="space-between" width="full" gap="2">
          <Box display="flex" alignItems="center">
            {icon}
            {valueRender &&
              (shouldTruncateValueRender ? (
                <Text size="xSmall" color="secondaryContentText" lines="1">
                  {valueRender}
                </Text>
              ) : (
                valueRender
              ))}
          </Box>
          {clearable && !!value && (
            <ButtonIcon
              icon={<IconSolidXCircle />}
              onClick={() => onChange(undefined)}
              size="xSmall"
              emphasis="none"
              kind="secondary"
            />
          )}
        </Stack>
      </Select>
      <SelectPopover
        store={select}
        className={clsx([selectPopover, popoverCssClass])}
        gutter={4}
        autoFocusOnHide={false}
        onClose={onClose}
        fixed
      >
        <PopoverArrow size={0} />
        {onChangeQuery !== undefined && (
          <div
            className={clsx(comboboxWrapper, {
              [comboboxHasResults]: allOptions.length > 0 || isLoading || !!emptyStateRender,
            })}
          >
            <IconSolidSearch />
            <Combobox
              store={combobox}
              type="text"
              autoSelect
              autoComplete="none"
              placeholder={queryPlaceholder}
              className={styles_combobox}
            ></Combobox>
          </div>
        )}
        <ComboboxList store={combobox} className={clsx([comboboxList, 'hide-scrollbar'])}>
          {emptyStateRender && !isLoading && allOptions.length === 0 && (
            <div className={clsx([selectItem, statePlaceholder])}>{emptyStateRender}</div>
          )}
          {select.useState('open') &&
            allOptions.map((option: Option) => (
              <ComboboxItem
                focusOnHover
                key={option.key}
                className={selectItem}
                render={
                  <SelectItem value={option.key}>
                    {isMultiselect && (
                      <div
                        className={checkbox}
                        style={{
                          backgroundColor: valueSet.has(option.key)
                            ? vars.theme.interactive.fill.primary.enabled
                            : 'white',
                        }}
                      >
                        <IconSolidCheckCircle color="white" />
                      </div>
                    )}
                    {option.render}
                  </SelectItem>
                }
              ></ComboboxItem>
            ))}
          {isLoading && <div className={clsx([selectItem, statePlaceholder])}>{loadingRender}</div>}
        </ComboboxList>
      </SelectPopover>
    </div>
  );
};
